/**
 * Anon identity claim (f/identity-ownership, migration 036).
 *
 * The database binds each guest id to the session that first writes it (the
 * anon_identities register). The exchange claims EXPLICITLY and EARLY — before
 * the chat mounts, before any live channel, before the confirm — so there is
 * never a moment where the id is visible (heartbeat, room) but unclaimed.
 *
 * ensureAnonIdentity():
 *   - mints the id if this browser has none (the scan is the commit action),
 *   - waits for the auth session, then claims it (claim_my_anon_id),
 *   - taken / locked / invalid → keeps the old id as an alias (its messages
 *     stay on this side via the sidedness rails), mints a fresh id, claims
 *     that, and logs anon_identity_rotated,
 *   - no_session / network error → resolves unclaimed; the next call retries
 *     (the server still claims atomically at the first write).
 * Memoized while in flight and once claimed. Never rejects.
 */
import { supabase, authReady } from '../supabase';
import { getAnonUserId, getOrCreateAnonUserId, rotateAnonUserId } from '../../utils/identityUtils';
import { logEvent } from '../analytics/eventLogger';

const OWNED = new Set(['owned', 'converted']);
const ROTATE = new Set(['taken', 'locked', 'invalid']);

let inFlight = null;

const claim = async (anonUserId) => {
  try {
    const { data, error } = await supabase.rpc('claim_my_anon_id', {
      p_anon_user_id: anonUserId,
    });
    if (error) {
      console.warn('[ANON_IDENTITY] claim failed:', error.message);
      return 'error';
    }
    return data;
  } catch (err) {
    console.warn('[ANON_IDENTITY] claim threw:', err);
    return 'error';
  }
};

const run = async () => {
  const first = getOrCreateAnonUserId();
  await authReady;
  const status = await claim(first);
  if (OWNED.has(status)) return { anonUserId: first, status, claimed: true };
  if (!ROTATE.has(status)) return { anonUserId: first, status, claimed: false };

  // Not ours to use: start a fresh identity, keep the old one attributable.
  const fresh = rotateAnonUserId();
  const freshStatus = await claim(fresh);
  logEvent('anon_identity_rotated', { reason: status, claimed: OWNED.has(freshStatus) });
  return {
    anonUserId: fresh,
    status: OWNED.has(freshStatus) ? 'rotated' : freshStatus,
    claimed: OWNED.has(freshStatus),
  };
};

export const ensureAnonIdentity = () => {
  if (!inFlight) {
    inFlight = run()
      .catch((err) => {
        console.warn('[ANON_IDENTITY] ensure failed:', err);
        return { anonUserId: getAnonUserId(), status: 'error', claimed: false };
      })
      .then((result) => {
        if (!result.claimed) inFlight = null; // retry on the next call
        return result;
      });
  }
  return inFlight;
};

/** Test-only: forget the memoized claim. */
export const resetAnonIdentityForTests = () => {
  inFlight = null;
};
